import React, { useState, createContext, useRef } from 'react'
import ReactToPrint from 'react-to-print'
import { IPriceComparison, IProduct } from './types'
import ProductCard from './components/ProductCard'
import SharedFeatures from './components/SharedFeatures'
import getIdFromString from '../../utils/getIdFromString'
import Print from '@tdcerhverv/parrotfish/dist/icons/Icons/Print.svg'
import PriceComparisonCarousel from './components/PriceComparisonCarousel'
import useMediaQuery from '@mui/material/useMediaQuery'
// MUI
import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/system'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'

import useMobileDisplay from '../../hooks/useMobileDisplay'

export interface IPriceComparisonContext {
  descriptionHeight: number
  setDescriptionHeight?: (value: number) => void
}

export const PriceComparisonContext = createContext<IPriceComparisonContext>({
  descriptionHeight: 0,
  setDescriptionHeight: (_value: number) => {
    //do nothing
  },
})

const PriceComparison = ({
  titlesAreBlue,
  title,
  products,
  noofSlide,
  sharedFeatures,
  priceDesclaimerText,
  sharedFeatureTitle,
  newSharedFeatures,
  sharedFeaturesAreStacked,
  trackingIdModule = 'price-module', //default value can be overridden by Linevisitation
}: IPriceComparison) => {
  const ref = useRef(null)
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0)
  const isMobile = useMobileDisplay()
  const priceTableId = `price-box-${encodeURI(getIdFromString(title!))}`
  let slideLength
  if (noofSlide) slideLength = noofSlide
  else slideLength = products && products?.length > 4 ? 4 : products?.length
  const isMdDevice = useMediaQuery(useTheme().breakpoints.between('md', 'xl'))
  const isSmDevice = useMediaQuery(useTheme().breakpoints.between('sm', 'lg'))
  const isXsDevice = useMediaQuery(useTheme().breakpoints.down('sm'))
  if (isMdDevice) {
    if (products && products?.length >= 3) slideLength = 3
    else slideLength = products?.length
  } else if (isSmDevice) {
    if (products && products?.length >= 2) slideLength = 2
    else slideLength = products?.length
  } else if (isXsDevice) {
    slideLength = 1
  }

  const StylePrintTable = styled('table')(({ theme }) => ({
    color: theme.palette.common.black,
    backgroundColor: theme.palette.common.white,
    borderSpacing: '0px',
    width: '100%',
    textAlign: 'initial',

    // Hide the table on the web page
    '@media screen': {
      display: 'none',
    },

    '@media print': {
      display: 'table',
      borderCollapse: 'collapse',
      border: `1px solid ${theme.palette.grey[300]}`,
    },
  }))

  const StyledTr = styled('tr')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
  }))

  const StyledTd = styled('td')(({ theme }) => ({
    flex: '1 0 30%',
    padding: '0px',
  }))

  const styles = {
    priceDisclaimer: {
      display: `block`,
      marginTop: '32px',
      whiteSpace: 'pre-line',
      color: 'inherit',
    },
    priceComparisonControls: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginRight: '57px',
    },
  }

  const pageStyles = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
     
    }
   
    #${priceTableId} {
      margin-top:70px; 
    }
    .print-table{
      display: block !important;
    }
   
    .price-comparison-carousel{
      display: none !important;
    }
    .print-price-disclaimer{
      margin:32px !important
    }
    .print-button-container {
      display: none !important;
    }
    .bullet-control-container{
      display: none !important;
    }
    .MuiListItem-root {
     line-height:40px !important;
    }
    [class*=recommeded] {
      margin-top: -27px !important
    }
    button {
      display: none !important;
    }
    @page {
      margin-top:20px !important;
      size: A4 landscape;
    }
  } `

  return (
    <PriceComparisonContext.Provider value={{ descriptionHeight, setDescriptionHeight }}>
      <div>
        {!isMobile && (
          <ReactToPrint
            removeAfterPrint={true}
            documentTitle={title || ''}
            trigger={() => (
              <Box sx={{ ...styles.priceComparisonControls, className: 'print-button-container' }}>
                <Button
                  id={`print-button-${encodeURI(getIdFromString(title || ''))}`}
                  startIcon={<SvgIcon component={Print} viewBox="0 0 48 48" />}
                  size="small"
                  variant="text"
                >
                  Print
                </Button>
              </Box>
            )}
            pageStyle={pageStyles}
            content={() => ref.current}
          />
        )}

        <PriceComparisonCarousel
          title="PriceComparison Carousel"
          numberOfSlidesInView={slideLength || 4} // Adjust the number of visible slides per view
          render={() => {
            if (sharedFeatures !== undefined && (sharedFeatures?.sharedFeatureItems?.length ?? 0) > 0) {
              // Render with sharedFeatures
              return <SharedFeatures {...sharedFeatures} />
            } else if (Array.isArray(newSharedFeatures) && newSharedFeatures?.length > 0) {
              // Render with newSharedFeatures
              return (
                <SharedFeatures
                  sharedFeaturesTitle={sharedFeatureTitle}
                  sharedFeatureItems={newSharedFeatures}
                  sharedFeaturesAreStacked={sharedFeaturesAreStacked}
                />
              )
            } else {
              // Render nothing if neither is available
              return <></>
            }
          }}
        >
          {products?.map((product: React.JSX.IntrinsicAttributes & IProduct, index: number) => {
            return (
              <ProductCard
                key={index}
                trackingIdModule={trackingIdModule}
                {...product}
                titleIsBlue={titlesAreBlue || false}
              />
            )
          })}
        </PriceComparisonCarousel>
        {/* adding this table only  for print layout purpose  */}
        <div ref={ref} id={priceTableId}>
          <StylePrintTable>
            <tbody>
              {products &&
                products
                  .reduce(
                    (rows: React.ReactNode[][], product: React.JSX.IntrinsicAttributes & IProduct, index: number) => {
                      if (index % 3 === 0) rows.push([])
                      rows[rows.length - 1].push(
                        <StyledTd key={index}>
                          <ProductCard
                            key={index}
                            trackingIdModule={trackingIdModule}
                            {...product}
                            titleIsBlue={titlesAreBlue || true}
                          />
                        </StyledTd>,
                      )
                      return rows
                    },
                    [],
                  )
                  .map((row, rowIndex) => <StyledTr key={rowIndex}>{row}</StyledTr>)}
              {sharedFeatures && sharedFeatures.sharedFeatureItems?.length > 0 ? (
                <SharedFeatures {...sharedFeatures} />
              ) : newSharedFeatures && newSharedFeatures?.length > 0 ? (
                <SharedFeatures
                  sharedFeaturesTitle={sharedFeatureTitle}
                  sharedFeatureItems={newSharedFeatures}
                  sharedFeaturesAreStacked={sharedFeaturesAreStacked}
                />
              ) : (
                <></>
              )}
            </tbody>
          </StylePrintTable>

          <Typography sx={styles.priceDisclaimer} variant="caption">
            {priceDesclaimerText?.priceDesclaimerText}
          </Typography>
        </div>
      </div>
    </PriceComparisonContext.Provider>
  )
}

export default PriceComparison
